/**
 * 메인/수업 프레젠을 보조 모니터에 열기 전 앱 확인 모달.
 *
 * - 권한 '묻기 전'(prompt) + 확장 모니터: 브라우저 팝업 전에 앱 모달로 먼저 설명 →
 *   [허용] 클릭(사용자 제스처) 안에서 원래 열기 동작을 실행 → openPresentationWindow가
 *   창을 열면서 크롬 '창 관리' 권한을 묻고, 허용 시 보조 모니터로 옮긴다.
 * - 권한 거부(denied): 다시 켜는 방법 안내 + '다시 보지 않기' + [이 화면에서 열기].
 * - 단일 모니터 / 미지원 / 이미 허용: 모달 없이 바로 연다.
 */
import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import {
  getPresentationScreenStatus,
  refreshPresentationScreenStatus,
  requestPresentationScreenPermission,
  setOpenPresentationHereOnce,
  type PresentationScreenStatus,
} from '../../utils/openPresentationWindow';

const DENIED_DISMISS_KEY = 'cp.presentationScreen.deniedGuideDismissed';

function isDeniedGuideDismissed(): boolean {
  try {
    return window.localStorage.getItem(DENIED_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function setDeniedGuideDismissed(v: boolean): void {
  try {
    if (v) window.localStorage.setItem(DENIED_DISMISS_KEY, '1');
    else window.localStorage.removeItem(DENIED_DISMISS_KEY);
  } catch {
    /* ignore */
  }
}

let host: HTMLDivElement | null = null;
let root: Root | null = null;

function closeModal(): void {
  root?.render(<></>);
}

function mount(node: React.ReactElement): void {
  if (typeof document === 'undefined') return;
  if (!host) {
    host = document.createElement('div');
    host.className = 'cp-presentation-screen-gate-host';
    document.body.appendChild(host);
    root = createRoot(host);
  }
  root!.render(node);
}

const DENIED_STEPS = '주소창 왼쪽 아이콘 → 사이트 설정 → "창 관리"를 허용으로 바꾼 뒤, 프레젠을 다시 열어 주세요.';

type ShellProps = {
  title: string;
  desc?: React.ReactNode;
  onDismiss: () => void;
  children: React.ReactNode;
};

const ModalShell: React.FC<ShellProps> = ({ title, desc, onDismiss, children }) => {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onDismiss();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onDismiss]);
  return (
    <div
      className="cp-pin-confirm-overlay cp-pin-confirm-overlay--viewport cp-delete-hold-confirm-overlay"
      role="presentation"
      onClick={onDismiss}
    >
      <div
        className="cp-pin-confirm cp-pin-confirm--warn cp-presentation-screen-gate"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="cp-pin-confirm-warn-title">{title}</p>
        {desc ? <div className="cp-pin-confirm-warn-desc">{desc}</div> : null}
        {children}
      </div>
    </div>
  );
};

const PromptModal: React.FC<{ run: () => void }> = ({ run }) => (
  <ModalShell
    title="보조 모니터에 프레젠을 열까요?"
    desc={
      <>
        <p>[허용]을 누르면 브라우저가 &lsquo;창 관리&rsquo; 권한을 한 번 물어봐요.</p>
        <p>거기서도 허용하면 프레젠이 보조 모니터에 꽉 찬 창으로 열리고, 다음부터는 묻지 않아요.</p>
      </>
    }
    onDismiss={closeModal}
  >
    <div className="cp-pin-confirm-actions cp-pin-confirm-actions--split">
      <button
        type="button"
        className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
        onClick={() => {
          closeModal();
          setOpenPresentationHereOnce();
          run();
        }}
      >
        이 화면에서 열기
      </button>
      <button
        type="button"
        className="cp-pin-modal-btn cp-pin-modal-btn--primary"
        onClick={() => {
          closeModal();
          // 같은 클릭 안에서 실행: openPresentationWindow가 권한 요청 → 창 열기 → 보조 모니터 이동
          run();
        }}
      >
        허용
      </button>
    </div>
  </ModalShell>
);

const DeniedModal: React.FC<{ run: () => void }> = ({ run }) => {
  const [dontShow, setDontShow] = React.useState(false);
  return (
    <ModalShell
      title="보조 모니터 권한이 꺼져 있어요"
      desc={
        <>
          <p>예전에 &lsquo;창 관리&rsquo; 권한을 허용하지 않아서 지금 화면에 열려요.</p>
          <p>{DENIED_STEPS}</p>
        </>
      }
      onDismiss={closeModal}
    >
      <label className="cp-presentation-screen-gate-check">
        <input type="checkbox" checked={dontShow} onChange={(e) => setDontShow(e.target.checked)} />
        다시 보지 않기
      </label>
      <div className="cp-pin-confirm-actions cp-pin-confirm-actions--split">
        <button
          type="button"
          className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
          onClick={() => {
            if (dontShow) setDeniedGuideDismissed(true);
            closeModal();
          }}
        >
          닫기
        </button>
        <button
          type="button"
          className="cp-pin-modal-btn cp-pin-modal-btn--primary"
          onClick={() => {
            if (dontShow) setDeniedGuideDismissed(true);
            closeModal();
            run();
          }}
        >
          이 화면에서 열기
        </button>
      </div>
    </ModalShell>
  );
};

/**
 * 프레젠 열기 동작을 감싼다. 모달이 필요 없으면 즉시(같은 클릭 안에서) 실행.
 * `run` 안의 window.open은 모달 버튼 클릭(새 사용자 제스처) 안에서 실행된다.
 */
export function gatePresentationOpen(run: () => void): void {
  const status = getPresentationScreenStatus();
  if (status === 'prompt') {
    mount(<PromptModal run={run} />);
    return;
  }
  if (status === 'denied' && !isDeniedGuideDismissed()) {
    mount(<DeniedModal run={run} />);
    return;
  }
  run();
}

const STATUS_TEXT: Record<PresentationScreenStatus, string> = {
  granted: '허용됨 — 모니터가 2대 이상이면 프레젠이 보조 모니터에 열려요.',
  prompt: '아직 허용하지 않았어요. 아래 [허용하기]를 누르면 브라우저가 권한을 물어봐요.',
  denied: `허용하지 않음 — ${DENIED_STEPS}`,
  single: '지금은 모니터가 1대라서 기존처럼 이 화면에 열려요.',
  unsupported: '이 브라우저는 보조 모니터 열기를 지원하지 않아요. 크롬이나 엣지에서 사용해 주세요.',
  unknown: '모니터 상태를 확인할 수 없어요. 모니터를 연결한 뒤 다시 확인해 주세요.',
};

const SettingsModal: React.FC = () => {
  const [status, setStatus] = React.useState<PresentationScreenStatus>(getPresentationScreenStatus());
  const [busy, setBusy] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(isDeniedGuideDismissed());
  React.useEffect(() => {
    void refreshPresentationScreenStatus().then(setStatus);
  }, []);
  return (
    <ModalShell title="보조 모니터 프레젠" desc={<p>{STATUS_TEXT[status]}</p>} onDismiss={closeModal}>
      {dismissed ? (
        <label className="cp-presentation-screen-gate-check">
          <input
            type="checkbox"
            checked={!dismissed}
            onChange={() => {
              setDeniedGuideDismissed(false);
              setDismissed(false);
            }}
          />
          권한 안내 다시 보기
        </label>
      ) : null}
      <div className="cp-pin-confirm-actions cp-pin-confirm-actions--split">
        <button type="button" className="cp-pin-modal-btn cp-pin-modal-btn--secondary" onClick={closeModal}>
          닫기
        </button>
        {status === 'prompt' || status === 'unknown' ? (
          <button
            type="button"
            className="cp-pin-modal-btn cp-pin-modal-btn--primary"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void requestPresentationScreenPermission()
                .then(() => refreshPresentationScreenStatus())
                .then(setStatus)
                .finally(() => setBusy(false));
            }}
          >
            허용하기
          </button>
        ) : (
          <button
            type="button"
            className="cp-pin-modal-btn cp-pin-modal-btn--primary"
            onClick={() => void refreshPresentationScreenStatus().then(setStatus)}
          >
            다시 확인
          </button>
        )}
      </div>
    </ModalShell>
  );
};

/** 계정 메뉴 등에서 상태 확인·허용·안내를 다시 열 때. */
export function showPresentationScreenSettings(): void {
  mount(<SettingsModal />);
}
